import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { ToastContainer, toast } from 'react-toastify';
import ManufacturerItem from '../../components/ManufacturerItem';
import { Input } from '../../components/Common/Input';

const SelectAllCheckbox = ({ checked, indeterminate, onChange, label }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    return (
        <label className="select-manufacturers__select-all-label">
            <input type="checkbox" ref={ref} checked={checked} onChange={onChange} />
            {label && <span style={{ lineHeight: 1 }}>{label}</span>}
        </label>
    );
};

const toastOptions = {
    position: 'top-center',
    autoClose: 700,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
};

export const showComment = manufacturer => {
    switch (manufacturer.comment) {
        case 'manufacturers.comment.beta.new':
            return "This brand is new in our platform and we don't yet have enough data to determine the stability.";
        case 'manufacturers.comment.beta.unstable':
            return 'Stability is rated low. This means that it sometimes might affect the service. For example it might sometimes take longer before we detect that the EV is connected and optimization can begin.';
        case 'manufacturers.comment.alpha':
            return 'This integration is in alpha state and we have not yet determined its stability.';
        case 'manufacturers.comment.development':
            return 'Under development...';
        case 'manufacturers.comment.development.deprecated':
            return 'Deprecated';
        case 'manufacturers.comment.beta.location_sharing':
            return 'Interventions often needed from the end user, for example enabling location sharing for the vehicle or a software update. Once this is done the smart charging will work.';
        case 'manufacturers.comment.beta.delayed_data':
            return 'Delayed data can sometimes cause problems with smart charging and visualization.';
        default:
            return '';
    }
};

const SelectBrand = () => {
    const [manufacturers, setManufacturers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [savedIds, setSavedIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const manufacturerTypesTitles = {
        ev: 'Electric vehicles',
        cs: 'Charge stations',
        hvac: 'Heating',
        si: 'Solar inverters',
        inv: 'Battery & Solar',
        sm: 'Smart Meters',
        bat: 'Batteries',
    };

    useEffect(async () => {
        let manufacturers = (await api.get_manufacturers()).data;
        const enabledIds = manufacturers.filter(x => x.enabled === true).map(x => x.id);
        setSelectedIds(enabledIds);
        setSavedIds(enabledIds);

        if (process.env.REACT_APP_ENV === 'production') {
            setManufacturers(manufacturers.filter(m => m.state === 'beta' || m.state === 'stable'));
        } else {
            setManufacturers(manufacturers);
        }
    }, []);

    const hasUnsavedChanges = JSON.stringify([...selectedIds].sort()) !== JSON.stringify([...savedIds].sort());

    const filteredManufacturers = searchQuery ? manufacturers.filter(m => m.label.toLowerCase().includes(searchQuery.toLowerCase())) : manufacturers;

    const handleSelect = checked => {
        if (selectedIds.includes(checked)) {
            setSelectedIds(selectedIds.filter(x => x !== checked));
        } else {
            setSelectedIds([...selectedIds, checked]);
        }
    };

    const visibleIds = filteredManufacturers.map(m => m.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    const someSelected = visibleIds.some(id => selectedIds.includes(id));

    const handleSelectAll = () => {
        if (allSelected) {
            const drop = new Set(visibleIds);
            setSelectedIds(selectedIds.filter(id => !drop.has(id)));
        } else {
            setSelectedIds([...new Set([...selectedIds, ...visibleIds])]);
        }
    };

    const handleSelectSection = sectionIds => {
        const allSectionSelected = sectionIds.every(id => selectedIds.includes(id));
        if (allSectionSelected) {
            const drop = new Set(sectionIds);
            setSelectedIds(selectedIds.filter(id => !drop.has(id)));
        } else {
            setSelectedIds([...new Set([...selectedIds, ...sectionIds])]);
        }
    };

    const handleSave = () => {
        api.set_manufacturers(selectedIds).then(
            _response => {
                setSavedIds([...selectedIds]);
                toast.success(`Brand selection saved!`, toastOptions);
            },
            _error => {
                toast.error(`Error while saving brand selection`, toastOptions);
            }
        );
    };

    const handleDiscard = () => {
        setSelectedIds([...savedIds]);
    };

    return (
        <div className="retailer-api select-manufacturers__container">
            <ToastContainer />
            <div className="select-manufacturers__header">
                <div>
                    <h2 className="select-manufacturers__header--main-title">Select Manufacturers</h2>
                    <p className="select-manufacturers__header--subtitle">Choose which manufacturers appear in your customer-facing experience.</p>
                </div>
                <div className="select-manufacturers__counter">
                    <div className="select-manufacturers__counter--numbers">
                        <span className="select-manufacturers__counter--selected">{selectedIds.length}</span>
                        <span className="select-manufacturers__counter--total">/{manufacturers.length}</span>
                    </div>
                    <span className="select-manufacturers__counter--label">SELECTED</span>
                </div>
            </div>

            <div className="select-manufacturers__sticky-toolbar">
                <div className="select-manufacturers__sticky-toolbar--toolbar-left">
                    <SelectAllCheckbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={handleSelectAll}
                        label="Select all manufacturers"
                    />
                    <div className="select-manufacturers__sticky-toolbar--search-wrapper">
                        <Input
                            type="text"
                            className="select-manufacturers__sticky-toolbar--search"
                            placeholder="Search manufacturer..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="select-manufacturers__sticky-toolbar--toolbar-actions">
                    <span className={`select-manufacturers__sticky-toolbar--save-status ${hasUnsavedChanges ? 'unsaved' : ''}`}>
                        <span className="select-manufacturers__sticky-toolbar--save-status-dot" />
                        {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
                    </span>
                    <div className="select-manufacturers__sticky-toolbar--toolbar-buttons">
                        <button className="select-manufacturers__sticky-toolbar--discard-btn" onClick={handleDiscard} disabled={!hasUnsavedChanges}>
                            <svg
                                className="select-manufacturers__sticky-toolbar--discard-btn-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="9 14 4 9 9 4" />
                                <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                            </svg>
                            Discard
                        </button>
                        <button
                            className={`select-manufacturers__sticky-toolbar--save-btn ${hasUnsavedChanges ? 'active' : ''}`}
                            onClick={handleSave}
                        >
                            Save changes
                        </button>
                    </div>
                </div>
            </div>

            <div>
                {Object.keys(manufacturerTypesTitles).map(type => {
                    const sectionManufacturers = filteredManufacturers.filter(m => m.type === type);
                    const visibleSectionIds = sectionManufacturers.map(m => m.id);
                    const selectedInSection = visibleSectionIds.filter(id => selectedIds.includes(id)).length;
                    const allSectionSelected = visibleSectionIds.length > 0 && selectedInSection === visibleSectionIds.length;
                    const someSectionSelected = selectedInSection > 0;

                    return (
                        <div key={type}>
                            {sectionManufacturers.length > 0 && (
                                <div>
                                    <div className="select-manufacturers__section-header">
                                        <div className="select-manufacturers__section-header--left">
                                            <SelectAllCheckbox
                                                checked={allSectionSelected}
                                                indeterminate={someSectionSelected && !allSectionSelected}
                                                onChange={() => handleSelectSection(visibleSectionIds)}
                                            />
                                            <h4 className="select-manufacturers__section-header--section-title">{manufacturerTypesTitles[type]}</h4>
                                            <span className="select-manufacturers__section-header--section-count">
                                                {selectedInSection} / {visibleSectionIds.length} SELECTED
                                            </span>
                                        </div>
                                    </div>
                                    <div className="select-manufacturers__brands-wrapper">
                                        {sectionManufacturers.map(manufacturer => (
                                            <ManufacturerItem
                                                key={manufacturer.id}
                                                manufacturer={manufacturer}
                                                selectedIds={selectedIds}
                                                handleSelect={handleSelect}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SelectBrand;
